import React from "react";
import { Page, Text, View, StyleSheet, Image, Document } from "@react-pdf/renderer";
import { hasElements } from "utils/arrays";
import { dateToFullString, dateToString } from "utils/dateUtils";
import { Tenant } from "utils/tenants";
import { formatCurrency } from "utils/currency";

// Create styles
const styles = StyleSheet.create({
    page: {
        paddingTop: 20,
        paddingBottom: 65,
        paddingHorizontal: 20,
    },
    header: {
        fontSize: 10,
        flexDirection: "row",
        marginBottom: 10,
        justifyContent: "flex-start",
    },
    image: {
        height: 64,
    },
    companyInfo: {
        marginLeft: 10,
    },
    companyName: {
        fontSize: 14,
        fontWeight: "bold",
    },
    title: {
        fontSize: 20,
    },
    divider: {
        width: "100%",
        height: 1,
        backgroundColor: "black",
        marginVertical: 10,
    },
    customerInfo: {
        fontSize: 10,
        fontWeight: "normal",
        flexDirection: "row",
        marginVertical: 20,
        justifyContent: "space-between",
    },
    customerName: {
        fontSize: 18,
        fontWeight: "bold",
    },
    fileInfo: {
        flexDirection: "row",
    },

    receivedSection: {
        fontSize: 14,
        fontWeight: "normal",
        flexDirection: "row",
        marginTop: 50,
        marginLeft: 10,
        justifyContent: "flex-start",
        width: "60%",
    },

    userName: {
        fontSize: 8,
    },

    pageNumber: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 20,
        right: 20,
        textAlign: "right",
        color: "grey",
    },

    footer: {
        position: "absolute",
        bottom: 40,
        left: 20,
        right: 20,
    },

    detail: {
        width: "100%",
    },

    tableHead: {
        flexDirection: "row",
        alignContent: "flex-start",
        border: "1px solid black",
    },

    columnHeader: {
        fontSize: 9,
        fontWeight: "bold",
        color: "blue",
        fontStyle: "italic",
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },

    row: {
        flexDirection: "row",
        alignContent: "flex-start",
        border: "1px solid black",
        borderTop: "none",
    },

    cell: {
        fontSize: 8,
        padding: 2,
        flex: 1,
        textAlign: "left",
        marginLeft: 5,
    },

    disclaimer: {
        fontSize: 10,
        fontStyle: "italic",
        textAlign: "left",
        marginTop: 30,
    },

    time: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 20,
        right: 20,
        textAlign: "left",
        color: "grey",
    },
    printedBy: {
        position: "absolute",
        fontSize: 10,
        bottom: 30,
        left: 10,
        right: 10,
        textAlign: "center",
        color: "grey",
    },
});

// Create Document Component
const MyDocument = ({ personalInfo, invoice, currentUser, currency }) => {
    const Money = ({ amount, currency }) => formatCurrency(amount, currency.locale, currency.isoCode);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.header} fixed>
                    <Image style={styles.image} src={Tenant.getLogo()} />
                    <View style={styles.companyInfo}>
                        <Text fixed style={styles.companyName}>
                            {invoice.issuer.name}
                        </Text>
                        <Text fixed>{invoice.issuer.address}</Text>
                        <Text fixed>Phone: {invoice.issuer.phoneNumber}</Text>
                        <Text fixed>Fax: {invoice.issuer.fax}</Text>
                        <Text fixed>Email: {invoice.issuer.email}</Text>
                    </View>
                    <View style={{ flex: 1 }} />
                    <View>
                        <Text fixed style={styles.title}>
                            Invoice
                        </Text>
                        <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                            No. {invoice.consecutive}
                        </Text>
                        <Text fixed style={{ fontSize: 10, alignSelf: "flex-end" }}>
                            {dateToString(invoice.issueDateString)}
                        </Text>
                    </View>
                </View>

                <View style={styles.divider} fixed />

                <View style={styles.customerInfo} fixed>
                    <View>
                        <Text fixed style={styles.customerName}>
                            {invoice.clientName}
                        </Text>
                        <Text fixed>{invoice.clientAddress}</Text>
                    </View>
                    <View style={{ flex: 1 }} />
                    {personalInfo && (
                        <View style={styles.fileInfo}>
                            <Text fixed style={{ fontSize: 12, marginRight: 20 }}>
                                File No
                            </Text>
                            <Text fixed style={{ fontSize: 14 }}>
                                {personalInfo.fileNo}
                            </Text>
                        </View>
                    )}
                </View>

                <View style={{ ...styles.divider, marginTop: 0, borderWidth: 2 }} fixed />

                <View style={styles.detail}>
                    <View style={styles.tableHead}>
                        <View style={{ ...styles.columnHeader, flexBasis: "40%" }}>
                            <Text>Service</Text>
                        </View>
                        <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "25%" }}>
                            <Text>Unit Price</Text>
                        </View>
                        <View style={{ ...styles.columnHeader, flexBasis: "10%" }}>
                            <Text>Quantity</Text>
                        </View>
                        <View style={{ ...styles.columnHeader, textAlign: "right", flexBasis: "25%" }}>
                            <Text>Total Price</Text>
                        </View>
                    </View>
                    {hasElements(invoice.invoiceDetails) &&
                        invoice.invoiceDetails.map((item) => (
                            <View key={`invoice-item-${item.id}`} style={styles.row}>
                                <View style={{ ...styles.cell, flexBasis: "40%" }}>
                                    <Text>{item.product.name}</Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.cell,
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        flexBasis: "25%",
                                    }}
                                >
                                    <Text>
                                        {item.unitPrice && Number(item.unitPrice) > 0 ? (
                                            <Money amount={item.unitPrice} currency={currency} />
                                        ) : (
                                            ""
                                        )}
                                    </Text>
                                </View>
                                <View style={{ ...styles.cell, flexBasis: "10%" }}>
                                    <Text>{item.quantity}</Text>
                                </View>
                                <View
                                    style={{
                                        ...styles.cell,
                                        textAlign: "right",
                                        fontWeight: "bold",
                                        flexBasis: "25%",
                                    }}
                                >
                                    <Text>
                                        {item.totalPrice && Number(item.totalPrice) > 0 ? (
                                            <Money amount={item.totalPrice} currency={currency} />
                                        ) : (
                                            ""
                                        )}
                                    </Text>
                                </View>
                            </View>
                        ))}
                    <View key="invoice-total" style={styles.row}>
                        <View style={{ ...styles.cell, flexBasis: "40%" }} />
                        <View
                            style={{
                                ...styles.cell,
                                flexBasis: "25%",
                            }}
                        />
                        <View style={{ ...styles.cell, flexBasis: "10%" }}>
                            <Text>TOTAL</Text>
                        </View>
                        <View
                            style={{
                                ...styles.cell,
                                textAlign: "right",
                                fontWeight: "bold",
                                flexBasis: "25%",
                            }}
                        >
                            <Text>
                                {invoice.totalAmount && Number(invoice.totalAmount) > 0 ? (
                                    <Money amount={invoice.totalAmount} currency={currency} />
                                ) : (
                                    ""
                                )}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={styles.disclaimer} fixed>
                        {""}
                    </Text>
                    <View style={styles.divider} fixed />
                    <Text style={{ fontSize: 10, marginBottom: 10 }} fixed>
                        Generated By: {invoice.stampCreationUser.name}
                    </Text>
                </View>
                <Text style={styles.time} render={() => dateToFullString(new Date())} fixed />
                <Text style={styles.printedBy} render={() => `Printed by ${currentUser.name}`} fixed />
                <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
            </Page>
        </Document>
    );
};

export default MyDocument;
